import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import AddFieldOnStep from '../../../../components/Pipelines/Modals/AddFieldOnStep'
import AddStepInPipeline from '../../../../components/Pipelines/Modals/AddStepInPipeline'
import ViewOrEditTicket from '../../../../components/Pipelines/Modals/ViewOrEditTicket'
import ViewOrEditTags from '../../../../components/Pipelines/Modals/ViewOrEditTags'
import AddIntegratedFieldModal from '../../../../components/Pipelines/Modals/AddIntegratedFieldModal'
import { coverUrlManager } from '../../../../utils/coverUrlManager'
import { getUserInitials } from '../../../../utils/userInitials'
import FillRequiredFieldsModal from '../../../../components/Pipelines/Modals/FillRequiredFieldsModal'
import TagComponent from '../../../../components/ui/Tag'
import styled from 'styled-components'
import { useDrag, useDrop } from 'react-dnd'
import { Step, Ticket } from '../types'
import { ScrollingPipeline, StepColumn, StepHeader, TicketItem, TicketList, CardTitle, MetaData, HorizontalDiv, AddInStep, InputForAdd, AddStep, TitleWithActions, ActionGroup, IconButton, CardMetaRow, CardMetaItem, StepCardCountBadge, StepExpiredBadge, CardFooter, CardCreatorAvatar, CardStageElapsedBadge, pipelineScrollProps, ticketListScrollProps } from '../style'
import { useDndScrolling } from 'react-dnd-scrolling'
import { CircleAlertIcon, MessageSquareText, Plus, User, X, SquarePen, Trash2, Check, Calendar, CheckSquare, Paperclip, MoreHorizontal, Eye, Copy, Link2, Archive, Settings, Pencil, Mail, FileText, Tag as TagIcon, Calculator, Circle, Clock, AlertTriangle, ExternalLink } from 'lucide-react'
import axios from '../../../../config/axios'
import { useNavigate, useOutletContext, useSearchParams, useLocation } from 'react-router-dom'
import { DateFilterField, usePipelineContext } from '../PipelineContext'
import TableView from '../TableView'
import { TypeMainOutlet } from '../..'
import { getModuleById } from '../../../../components/Pipelines/Modals/integrationModulesConfig'
import ConfirmationModal from '../../../../components/ModalConfirmation'

type DragItem = { id: number; stepId: number; index: number }
type TicketOrderCache = Record<number, Ticket[]>

const createRequestScheduler = (limit: number) => {
  let running = 0
  const queue: Array<() => void> = []

  const runNext = () => {
    if (running >= limit) return
    const next = queue.shift()
    if (!next) return
    running += 1
    next()
  }

  const schedule = <T,>(task: () => Promise<T>) => new Promise<T>((resolve, reject) => {
    const execute = () => {
      task()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          running -= 1
          runNext()
        })
    }
    queue.push(execute)
    runNext()
  })

  return schedule
}

const serviceStageRequestScheduler = createRequestScheduler(6)
const notFoundTolerantConfig = { validateStatus: (status: number) => status < 500 }

const getNestedValueByPath = (input: any, path: string): any => {
  const keys = String(path || '').split('.').filter(Boolean)
  let currentValue = input

  for (const key of keys) {
    if (currentValue === null || currentValue === undefined) return null

    if (Array.isArray(currentValue)) {
      const nextValues = currentValue
        .map((item) => item?.[key])
        .filter((item) => item !== null && item !== undefined)
        .flatMap((item) => Array.isArray(item) ? item : [item])

      if (!nextValues.length) return null
      currentValue = nextValues
      continue
    }

    currentValue = currentValue[key]
  }

  return currentValue
}

const extractDisplayNameFromIntegrationItem = (value: any): string => {
  if (!value || typeof value !== 'object') {
    return typeof value === 'string' ? value : ''
  }

  const directName = value.name || value.typeName || value.companyName || value.description || ''
  if (typeof directName === 'string' && directName.trim()) return directName.trim()

  const nestedKeys = ['Grade', 'Group', 'Material', 'CollectionColor', 'ItemColor', 'FamilyColor', 'ProductItemColor', 'color', 'Color']
  for (const nestedKey of nestedKeys) {
    const nestedValue = value[nestedKey]
    if (!nestedValue || typeof nestedValue !== 'object') continue
    const nestedName = nestedValue.name || nestedValue.description || ''
    if (typeof nestedName === 'string' && nestedName.trim()) return nestedName.trim()
  }

  return ''
}

const normalizeHexCandidate = (candidate: any): string | undefined => {
  if (typeof candidate !== 'string') return undefined
  const normalizedValue = candidate.trim()
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalizedValue)) return normalizedValue
  if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalizedValue)) return `#${normalizedValue}`
  return undefined
}

const extractSwatchHex = (value: any): string | undefined => {
  const visitedValues = new Set<any>()

  const findHex = (input: any, depth: number): string | undefined => {
    if (depth > 6) return undefined

    const directHex = normalizeHexCandidate(input)
    if (directHex) return directHex

    if (!input || typeof input !== 'object') return undefined
    if (visitedValues.has(input)) return undefined
    visitedValues.add(input)

    if (Array.isArray(input)) {
      for (const element of input) {
        const nestedHex = findHex(element, depth + 1)
        if (nestedHex) return nestedHex
      }
      return undefined
    }

    const prioritizedKeys = ['_swatchHex', 'hex', 'Hex', 'pantone', 'Pantone', 'color', 'Color', 'itemColor', 'ItemColor', 'collectionColor', 'CollectionColor']
    for (const prioritizedKey of prioritizedKeys) {
      if (!(prioritizedKey in input)) continue
      const nestedHex = findHex(input[prioritizedKey], depth + 1)
      if (nestedHex) return nestedHex
    }

    for (const nestedValue of Object.values(input)) {
      const nestedHex = findHex(nestedValue, depth + 1)
      if (nestedHex) return nestedHex
    }

    return undefined
  }

  return findHex(value, 0)
}

const resolveIntegrationResponseArray = (rawResponse: any, responseKey?: string): any[] => {
  if (responseKey && Array.isArray(rawResponse?.[responseKey])) {
    return rawResponse[responseKey]
  }

  if (Array.isArray(rawResponse)) return rawResponse
  if (Array.isArray(rawResponse?.data)) return rawResponse.data
  if (Array.isArray(rawResponse?.content)) return rawResponse.content
  if (Array.isArray(rawResponse?.items)) return rawResponse.items
  if (Array.isArray(rawResponse?.results)) return rawResponse.results

  if (rawResponse && typeof rawResponse === 'object') {
    const firstArrayKey = Object.keys(rawResponse).find((key) => Array.isArray(rawResponse[key]))
    if (firstArrayKey) {
      return rawResponse[firstArrayKey]
    }
  }

  return []
}

const normalizeSearchText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

const parseLocalDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split('T')[0].split('-').map(Number)
  return new Date(y, m - 1, d)
}

const getTicketDateForFilter = (ticket: Ticket, field: DateFilterField): string | undefined => {
  if (field === 'deadline') {
    return ticket.deadline || undefined
  }
  const startTime = ticket.externalData?.startTime
  if (startTime) return String(startTime)
  return ticket.createdAt || undefined
}

const isTicketInDateRange = (
  ticket: Ticket,
  field: DateFilterField,
  from: string,
  to: string,
): boolean => {
  const dateStr = getTicketDateForFilter(ticket, field)
  if (!dateStr) return false

  const ticketDay = parseLocalDate(dateStr)
  ticketDay.setHours(0, 0, 0, 0)

  if (from) {
    const fromDay = parseLocalDate(from)
    fromDay.setHours(0, 0, 0, 0)
    if (ticketDay < fromDay) return false
  }

  if (to) {
    const toDay = parseLocalDate(to)
    toDay.setHours(0, 0, 0, 0)
    if (ticketDay > toDay) return false
  }

  return true
}

const getFieldSearchValue = (field: any): string[] => {
  const values: string[] = []

  if (typeof field?.stringValue === 'string' && field.stringValue.trim()) {
    values.push(field.stringValue.trim())
  }

  if (typeof field?.bigStringValue === 'string' && field.bigStringValue.trim()) {
    values.push(field.bigStringValue.trim())
  }

  if (field?.integerValue !== null && field?.integerValue !== undefined) {
    values.push(String(field.integerValue))
  }

  if (field?.decimalValue !== null && field?.decimalValue !== undefined) {
    values.push(String(field.decimalValue))
  }

  if (field?.dateTimeValue) {
    const date = new Date(field.dateTimeValue)
    if (!Number.isNaN(date.getTime())) {
      values.push(date.toLocaleDateString('pt-BR'))
      values.push(date.toISOString())
    }
  }

  if (field?.booleanValue === true) {
    values.push('sim', 'verdadeiro', 'true')
  }

  if (field?.booleanValue === false) {
    values.push('nao', 'falso', 'false')
  }

  if (field?.integrationData) {
    values.push(JSON.stringify(field.integrationData))
  }

  if (Array.isArray(field?.integrationDataSelections) && field.integrationDataSelections.length > 0) {
    values.push(JSON.stringify(field.integrationDataSelections))
  }

  return values
}

const buildTicketSearchContent = (ticket: Ticket, stepName: string): string => {
  const fieldInTicketsContent = (ticket.fieldInTickets || []).flatMap((field) => [
    field?.pipelineField?.name || '',
    ...getFieldSearchValue(field),
  ])

  const tagsContent = (ticket.tags || []).map((tag) => tag?.tag?.name || '')

  const baseValues = [
    ticket.id,
    ticket.title,
    stepName,
    ticket.status || 'NEUTRO',
    ticket.deadline || '',
    ticket.createBy?.name || '',
    ticket.createBy?.email || '',
    ticket.urgency ? 'urgente' : '',
    ticket.createdAt || '',
    ...(ticket.fileInTickets || []).map((file: any) => [file?.originalName, file?.fileName, file?.mimeType, file?.title]).flat(),
    ...tagsContent,
    ...fieldInTicketsContent,
  ]

  return normalizeSearchText(baseValues.filter(Boolean).join(' '))
}

const ExternalDataRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 0 4px 0;
`

const ExternalDataChip = styled.span`
  font-size: 11px;
  color: #5A6878;
  line-height: 1.3;
  display: inline;
  word-break: break-word;
`

const CardActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6B7280;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
    color: #111827;
  }
`

interface CardActionMenuProps {
  top?: number
  left?: number
  direction?: 'up' | 'down'
}

const CardActionMenu = styled.div<CardActionMenuProps>`
  position: fixed;
  top: ${({ top }) => (typeof top === 'number' ? `${top}px` : 'unset')};
  left: ${({ left }) => (typeof left === 'number' ? `${left}px` : 'unset')};
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  min-width: 160px;
  padding: 8px 0;
  animation: ${({ direction }) => direction === 'up' ? 'slideUp' : 'slideDown'} 0.18s cubic-bezier(.4,0,.2,1);

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const CardActionMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.$danger ? '#DC2626' : '#303030'};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#FEF2F2' : '#f5f5f5'};
  }

  svg {
    color: ${props => props.$danger ? '#DC2626' : '#666666'};
    flex-shrink: 0;
  }
`

const StepSettingsButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #0090d3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 144, 211, 0.1);
    color: #0077b3;
  }
`

const StepAddCardButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #0090d3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 144, 211, 0.1);
    color: #0077b3;
  }
`

const StepSettingsMenu = styled.div<{ top?: number; left?: number }>`
  position: fixed;
  top: ${({ top }) => (typeof top === 'number' ? `${top}px` : 'unset')};
  left: ${({ left }) => (typeof left === 'number' ? `${left}px` : 'unset')};
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  min-width: 200px;
  padding: 8px 0;
  animation: slideDown 0.18s cubic-bezier(.4,0,.2,1);

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const StepSettingsMenuItem = styled.div<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.$danger ? '#DC2626' : '#303030'};
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.$danger ? '#FEF2F2' : '#f5f5f5'};
  }

  svg {
    color: ${props => props.$danger ? '#DC2626' : '#666666'};
    flex-shrink: 0;
  }
`

const StepMenuItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StepMenuDivider = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin: 8px 0;
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 20px;
  }

  span:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  input:checked + span {
    background-color: #0090D3;
  }

  input:checked + span:before {
    transform: translateX(16px);
  }
`

const RequiredCheckboxButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const RequiredCheckboxMark = styled.span<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${props => props.$active ? '#0090D3' : '#9CA3AF'};
  background-color: ${props => props.$active ? '#0090D3' : '#FFFFFF'};
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const CoverFieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`

const CoverFieldItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
`

const CoverFieldIcon = styled.div<{ $integrated?: boolean }>`
  width: 16px;
  min-width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1px solid ${({ $integrated }) => ($integrated ? '#8BC8E8' : '#C8D1DB')};
  background: ${({ $integrated }) => ($integrated ? '#E8F6FF' : '#F3F5F7')};
  color: ${({ $integrated }) => ($integrated ? '#0B7CB6' : '#5A6878')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`

const CoverFieldBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const CoverFieldLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #315f7f;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.2;
`

const CoverFieldValue = styled.span<{ $variant: 'metric' | 'text' }>`
  font-size: 13px;
  font-weight: 600;
  color: #18212a;
  line-height: 1.25;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CoverFieldSwatches = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const CoverFieldSwatch = styled.span<{ $hex: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid #C9D4DF;
  background-color: ${({ $hex }) => $hex};
  display: inline-block;
`

type StepFieldConfig = {
  pipelineField: any
  step: any
  position?: number
  isRequired?: boolean
  showOnCard?: boolean
}

type StepFieldDragItem = {
  pipelineFieldId: number
  stepId: number
  index: number
}

type ProductionStageAction = 'start' | 'pause' | 'finish'

const ProductionStageControls = ({ serviceStageId }: { serviceStageId: number }) => {
  const [lastEntry, setLastEntry] = useState<{ id: number; isPause: boolean; recordedAt: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState<ProductionStageAction | null>(null)

  const isRunning = lastEntry !== null && !lastEntry.isPause
  const hasStarted = lastEntry !== null

  const fetchEntries = useCallback(async () => {
    try {
      const lastRes = await serviceStageRequestScheduler(() =>
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/last-time-entry/${serviceStageId}`, notFoundTolerantConfig),
      )
      const lastPayload = lastRes.status === 404 ? null : lastRes.data
      setLastEntry(lastPayload || null)
    } catch {
      setLastEntry(null)
    } finally {
      setLoading(false)
    }
  }, [serviceStageId])

  useEffect(() => { fetchEntries() }, [fetchEntries])

  const formatClockTime = (recordedAt: string) =>
    new Date(recordedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  const confirmModalConfig = useMemo(() => {
    if (pendingAction === 'start') {
      return {
        title: hasStarted ? 'Confirmar continuação' : 'Confirmar início',
        message: hasStarted ? 'Deseja continuar este serviço?' : 'Deseja iniciar este serviço?',
        confirmVariant: 'primary' as const,
      }
    }
    if (pendingAction === 'pause') {
      return {
        title: 'Confirmar pausa',
        message: 'Deseja pausar este serviço?',
        confirmVariant: 'primary' as const,
      }
    }
    if (pendingAction === 'finish') {
      return {
        title: 'Confirmar finalização',
        message: 'Deseja finalizar este serviço? Esta ação conclui a etapa atual.',
        confirmVariant: 'danger' as const,
      }
    }
    return null
  }, [pendingAction, hasStarted])

  const executeAction = async (action: ProductionStageAction) => {
    setActionLoading(true)
    console.debug('[ProductionStageControls] Iniciando acao:', { action, serviceStageId, isRunning, hasStarted })
    try {
      if (action === 'start') {
        const body = hasStarted ? { continue: true } : {}
        console.debug('[ProductionStageControls] PATCH start:', { url: `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/${serviceStageId}`, body })
        const res = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/${serviceStageId}`, body)
        console.debug('[ProductionStageControls] PATCH start sucesso:', res.data)
      } else if (action === 'pause') {
        console.debug('[ProductionStageControls] PATCH pause:', { url: `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/finish-or-pause/${serviceStageId}`, isPause: true })
        const res = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/finish-or-pause/${serviceStageId}`, { isPause: true })
        console.debug('[ProductionStageControls] PATCH pause sucesso:', res.data)
      } else {
        console.debug('[ProductionStageControls] PATCH finish:', { url: `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/finish-or-pause/${serviceStageId}`, isPause: false })
        const res = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/finish-or-pause/${serviceStageId}`, { isPause: false })
        console.debug('[ProductionStageControls] PATCH finish sucesso:', res.data)
        console.debug('[ProductionStageControls] Disparando evento pipeline-request-sync')
        window.dispatchEvent(new Event('pipeline-request-sync'))
      }
      console.debug('[ProductionStageControls] Recarregando tempos locais...')
      await fetchEntries()
    } catch (err: any) {
      console.error('[ProductionStageControls] Erro ao executar acao:', action, err)
      const messages: Record<ProductionStageAction, string> = {
        start: 'Erro ao iniciar',
        pause: 'Erro ao pausar',
        finish: 'Erro ao finalizar',
      }
      alert(err?.response?.data?.message || messages[action])
    } finally {
      setActionLoading(false)
      setPendingAction(null)
    }
  }

  const openActionModal = (e: React.MouseEvent, action: ProductionStageAction) => {
    e.stopPropagation()
    setPendingAction(action)
  }

  if (loading) return <div style={{ fontSize: '11px', color: '#999', padding: '4px 0' }}>Carregando...</div>

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '8px', width: '100%' }}>
      {hasStarted && lastEntry && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px 8px',
          backgroundColor: isRunning ? '#E6F4FB' : '#f8f8f8',
          borderRadius: '6px',
          border: isRunning ? '1px solid #99D3EE' : '1px solid #e0e0e0',
          marginBottom: '6px',
        }}>
          <span style={{
            fontSize: '12px',
            fontWeight: 600,
            color: isRunning ? '#0090D3' : '#666',
          }}>
            {isRunning ? 'Iniciado em:' : 'Pausado em:'}{' '}
            {formatClockTime(lastEntry.recordedAt)}
          </span>
          {isRunning && (
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: '#22C55E',
              marginLeft: '6px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '4px' }}>
        <button
          onClick={(e) => openActionModal(e, 'start')}
          disabled={actionLoading || isRunning}
          style={{
            flex: 1,
            padding: '5px 0',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: actionLoading || isRunning ? '#ccc' : '#0090D3',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 600,
            cursor: actionLoading || isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          {hasStarted ? '▶ Continuar' : '▶ Iniciar'}
        </button>
        <button
          onClick={(e) => openActionModal(e, 'pause')}
          disabled={actionLoading || !isRunning}
          style={{
            flex: 1,
            padding: '5px 0',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: actionLoading || !isRunning ? '#ccc' : '#F59E0B',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 600,
            cursor: actionLoading || !isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          ⏸ Pausar
        </button>
        <button
          onClick={(e) => openActionModal(e, 'finish')}
          disabled={actionLoading || !isRunning}
          style={{
            flex: 1,
            padding: '5px 0',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: actionLoading || !isRunning ? '#ccc' : '#22C55E',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 600,
            cursor: actionLoading || !isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          ✓ Finalizar
        </button>
      </div>
      {confirmModalConfig && pendingAction && (
        <ConfirmationModal
          isOpen={true}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          actions={[
            {
              label: 'Cancelar',
              onClick: () => setPendingAction(null),
              variant: 'outline',
            },
            {
              label: 'Confirmar',
              onClick: () => executeAction(pendingAction),
              variant: confirmModalConfig.confirmVariant,
            },
          ]}
        />
      )}
    </div>
  )
}

const DraggableTicket = ({
  ticket,
  index,
  onReorder,
  onOpen,
  onEditClick,
  isEditing,
  titleDraft,
  onChangeTitle,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
  onDuplicate,
  onCopyLink,
  onToggleActive,
  onArchive,
  showArchived,
  coverFieldsInStep,
  resolveIntegratedColorSwatches,
  isModuleView,
  moduleType,
  onToggleSaleStatus,
  onAdvanceStep,
  isLastStep,
  externalData,
  isExpired,
  stepElapsedSeconds,
  stepExecutionTime,
}: {
  ticket: Ticket
  index: number
  onReorder: (stepId: number, dragIndex: number, hoverIndex: number) => void
  onOpen: () => void
  onEditClick: () => void
  isEditing: boolean
  titleDraft: string
  onChangeTitle: (value: string) => void
  onConfirmEdit: () => void
  onCancelEdit: () => void
  onDelete: () => void
  onDuplicate: () => void
  onCopyLink: () => void
  onToggleActive: () => void
  onArchive: () => void
  showArchived: boolean
  coverFieldsInStep: StepFieldConfig[]
  resolveIntegratedColorSwatches: (input: { moduleId?: string; fieldKey?: string; names: string[] }) => Promise<string[]>
  isModuleView?: boolean
  moduleType?: string | null
  onToggleSaleStatus?: () => void
  onAdvanceStep?: () => void
  isLastStep?: boolean
  externalData?: Record<string, any> | null
  isExpired?: boolean
  stepElapsedSeconds?: number | null
  stepExecutionTime?: number | null
}) => {
  const ref = useRef<HTMLLIElement | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; direction: 'up' | 'down' }>({ top: 0, left: 0, direction: 'down' })
  const [coverColorSwatchesByFieldId, setCoverColorSwatchesByFieldId] = useState<Record<number, string[]>>({})
  const { viewMode } = usePipelineContext()
  const navigate = useNavigate()
  const currentLocation = useLocation()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        if (menuButtonRef.current && menuButtonRef.current.contains(e.target as Node)) return
        setMenuOpen(false)
      }
    }
    const handleScrollOrResize = () => setMenuOpen(false)
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const handleToggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false)
      return
    }
    if (menuButtonRef.current) {
      const buttonRect = menuButtonRef.current.getBoundingClientRect()
      const menuHeight = 250
      const menuWidth = 160
      const buttonCenterX = buttonRect.left + (buttonRect.width / 2)
      let left = buttonCenterX - (menuWidth / 2)
      if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 10
      if (left < 10) left = 10
      let direction: 'up' | 'down' = 'down'
      let top = buttonRect.bottom + 5
      if (top + menuHeight > window.innerHeight) {
        direction = 'up'
        top = buttonRect.top - menuHeight - 5
      }
      setMenuPosition({ top, left, direction })
    }
    setMenuOpen(true)
  }

  const [coverImage, setCoverImage] = useState<string | undefined>();

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean}>({
    type: 'ticket',
    item: { id: ticket.id, stepId: ticket.stepId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop<DragItem, void, unknown>({
    accept: 'ticket',
    hover: (item, monitor) => {
      if (!ref.current) return
      if (item.id === ticket.id) return
      if (item.stepId !== ticket.stepId) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onReorder(ticket.stepId, dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))


  useEffect(() => {
    const updateCoverUrl = async () => {
      if (ticket.coverFile?.fileUrl && ticket.coverFile.id) {
        try {
          const url = await coverUrlManager.getCoverUrl(ticket.coverFile.id, ticket.coverFile.fileUrl);
          setCoverImage(url);
        } catch (error) {
          console.error('Erro ao obter URL da capa:', error);
          setCoverImage(ticket.coverFile.fileUrl);
        }
      } else {
        setCoverImage(undefined);
      }
    };

    updateCoverUrl();
  }, [ticket.coverFile]);

  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  const formatDeadline = (dateStr?: string) => {
    if (!dateStr) return null
    const date = parseLocalDate(dateStr)
    return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '')
  }

  const isDeadlineExpired = (dateStr?: string) => {
    if (!dateStr) return false
    const deadline = parseLocalDate(dateStr)
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return deadline < now
  }

  const completedTasks = ticket.tasks?.filter(t => t.completed).length || 0
  const totalTasks = ticket.tasks?.length || 0
  const attachmentsCount = ticket.fileInTickets?.length || 0
  const commentsCount = isModuleView && moduleType === 'production'
    ? (externalData?.currentServiceStageCommentsCount ?? 0)
    : (ticket.comments?.length || 0)

  const formatElapsed = (seconds: number) => {
    const d = Math.floor(seconds / (3600 * 24))
    const h = Math.floor((seconds % (3600 * 24)) / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (d > 0) return `${d}d${h > 0 ? ` ${h}h` : ''}`
    if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`
    if (m > 0) return `${m}m`
    return `${seconds}s`
  }

  const formatStageElapsed = (seconds: number) => {
    const days = Math.floor(seconds / 86400)
    if (days >= 1) return `${days}d`
    const hours = Math.floor(seconds / 3600)
    return `${hours}h`
  }

  const currentStageExecutionTime = ticket.externalData?.currentServiceStageExecutionTime != null
    ? Number(ticket.externalData.currentServiceStageExecutionTime)
    : (stepExecutionTime != null ? Number(stepExecutionTime) : null)

  const currentStageSetupTime = ticket.externalData?.currentServiceStageSetupTime != null
    ? Number(ticket.externalData.currentServiceStageSetupTime)
    : null

  const getCoverFieldValue = useCallback((field: any) => {
    const stringValue = typeof field?.stringValue === 'string' ? field.stringValue.trim() : ''
    if (stringValue) return stringValue

    const bigStringValue = typeof field?.bigStringValue === 'string' ? field.bigStringValue.trim() : ''
    if (bigStringValue) return bigStringValue

    if (field?.integerValue !== null && field?.integerValue !== undefined) {
      return String(field.integerValue)
    }

    if (field?.decimalValue !== null && field?.decimalValue !== undefined) {
      const decimal = Number(field.decimalValue)
      if (!Number.isNaN(decimal)) {
        return decimal.toLocaleString('pt-BR')
      }
      return String(field.decimalValue)
    }

    if (field?.dateTimeValue) {
      const date = new Date(field.dateTimeValue)
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR')
      }
    }

    if (field?.booleanValue === true) return 'Sim'
    if (field?.booleanValue === false) return 'Nao'

    return null
  }, [])

  const getCoverFieldIcon = useCallback((field: any) => {
    if (field?.pipelineField?.integrationConfig) {
      return <Link2 size={12} />
    }

    const normalizedTypeName = String(field?.pipelineField?.typeField?.name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

    if (normalizedTypeName.includes('checkbox')) {
      return <CheckSquare size={12} />
    }

    if (normalizedTypeName.includes('e-mail') || normalizedTypeName.includes('email')) {
      return <Mail size={12} />
    }

    if (normalizedTypeName.includes('cpf')) {
      return <User size={12} />
    }

    if (normalizedTypeName.includes('cnpj')) {
      return <TagIcon size={12} />
    }

    if (normalizedTypeName.includes('data')) {
      return <Calendar size={12} />
    }

    if (normalizedTypeName.includes('valor') || normalizedTypeName.includes('r$')) {
      return <Calculator size={12} />
    }

    if (normalizedTypeName.includes('texto') || normalizedTypeName.includes('telefone') || normalizedTypeName.includes('celular')) {
      return <MessageSquareText size={12} />
    }

    if (normalizedTypeName.includes('numero') || normalizedTypeName.includes('inteiro') || normalizedTypeName.includes('decimal')) {
      return <Calculator size={12} />
    }

    if (normalizedTypeName.includes('selecao') || normalizedTypeName.includes('select')) {
      return <Circle size={12} />
    }

    return <FileText size={12} />
  }, [])

  const getCoverFieldValueVariant = useCallback((field: any, value: string) => {
    const normalizedTypeName = String(field?.pipelineField?.typeField?.name || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

    const looksNumericValue = /^\d+([.,]\d+)?$/.test(String(value).trim())

    if (
      normalizedTypeName.includes('valor') ||
      normalizedTypeName.includes('r$') ||
      normalizedTypeName.includes('numero') ||
      normalizedTypeName.includes('inteiro') ||
      normalizedTypeName.includes('decimal')
    ) {
      return 'metric' as const
    }

    if (looksNumericValue && String(value).trim().length <= 8) {
      return 'metric' as const
    }

    return 'text' as const
  }, [])

  const coverFieldEntries = useMemo(() => {
    const seenFieldIds = new Set<number>()
    const orderedCoverFieldConfigs = (coverFieldsInStep || [])
      .filter((config) => config.showOnCard)
      .sort((a, b) => {
        const positionA = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER
        const positionB = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER
        if (positionA === positionB) {
          return Number(a.pipelineField?.id || 0) - Number(b.pipelineField?.id || 0)
        }
        return positionA - positionB
      })
      .filter((config) => {
        const id = Number(config.pipelineField?.id)
        if (!Number.isFinite(id) || seenFieldIds.has(id)) return false
        seenFieldIds.add(id)
        return true
      })

    if (!orderedCoverFieldConfigs.length || !Array.isArray(ticket.fieldInTickets)) return []

    const fieldById = new Map(
      ticket.fieldInTickets.map((field) => [Number(field.pipelineFieldId), field])
    )

    return orderedCoverFieldConfigs.reduce<any[]>((accumulator, config) => {
      const fieldId = Number(config.pipelineField?.id)
      if (!Number.isFinite(fieldId)) return accumulator

      const field = fieldById.get(fieldId)
      if (!field) return accumulator

      const value = getCoverFieldValue(field)
      if (!value) return accumulator

      accumulator.push({
        id: fieldId,
        label: field.pipelineField?.name || config.pipelineField?.name || 'Campo',
        value,
        integrated: Boolean(field?.pipelineField?.integrationConfig),
        integrationModuleId: field?.pipelineField?.integrationConfig?.moduleId,
        integrationFieldKey: field?.pipelineField?.integrationConfig?.fieldKey,
        isIntegratedColorField: Boolean(field?.pipelineField?.integrationConfig) && (
          normalizeSearchText(field?.pipelineField?.integrationConfig?.fieldKey || '').includes('color') ||
          normalizeSearchText(field?.pipelineField?.integrationConfig?.fieldKey || '').includes('cor') ||
          normalizeSearchText(field?.pipelineField?.name || '').includes('cor')
        ),
        icon: getCoverFieldIcon(field),
        variant: getCoverFieldValueVariant(field, String(value))
      })

      return accumulator
    }, [])
  }, [coverFieldsInStep, ticket.fieldInTickets, getCoverFieldValue, getCoverFieldIcon, getCoverFieldValueVariant])

  useEffect(() => {
    let isMounted = true

    const resolveSwatches = async () => {
      const nextSwatchesByFieldId: Record<number, string[]> = {}

      for (const fieldEntry of coverFieldEntries) {
        if (!fieldEntry.isIntegratedColorField || !fieldEntry.integrated) continue
        const fieldValue = String(fieldEntry.value || '')
        const colorNames = fieldValue
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item.length > 0)

        if (!colorNames.length) continue

        const swatches = await resolveIntegratedColorSwatches({
          moduleId: fieldEntry.integrationModuleId,
          fieldKey: fieldEntry.integrationFieldKey,
          names: colorNames,
        })

        if (swatches.length > 0) {
          nextSwatchesByFieldId[fieldEntry.id] = swatches
        }
      }

      if (isMounted) {
        setCoverColorSwatchesByFieldId(nextSwatchesByFieldId)
      }
    }

    resolveSwatches()

    return () => {
      isMounted = false
    }
  }, [coverFieldEntries, resolveIntegratedColorSwatches])

  const statusVisual = useMemo(() => {
    let effectiveStatus: string = ticket.status || 'NEUTRO'

    if (isModuleView && moduleType === 'sales' && ticket.saleStatus) {
      if (ticket.saleStatus === 'closed' || ticket.saleStatus === 'won') effectiveStatus = 'GANHOU'
      else if (ticket.saleStatus === 'cancelled' || ticket.saleStatus === 'lost') effectiveStatus = 'PERDEU'
      else effectiveStatus = 'NEUTRO'
    }

    if (effectiveStatus === 'GANHOU') {
      return {
        backgroundColor: '#E9FBEF',
        border: '1px solid #A7E2BA'
      }
    }
    if (effectiveStatus === 'PERDEU') {
      return {
        backgroundColor: '#FDECEC',
        border: '1px solid #F3B4B4'
      }
    }
    return {
      backgroundColor: '#FFFFFF',
      border: '1px solid #EFEFEF'
    }
  }, [ticket.status, ticket.saleStatus, isModuleView, moduleType])

  const externalDataEntries = useMemo(() => {
    if (!isModuleView || !externalData || typeof externalData !== 'object') return []
    const labelMap: Record<string, string> = {
      clientName: 'Cliente',
      productName: 'Produto',
      saleNumber: 'Pedido',
      npc: 'NPC',
      sellerName: 'Vendedor',
      collectionName: 'Coleção',
      codeOfOP: 'OP',
      currentStageName: 'Serviço',
    }
    const hiddenKeys = new Set(['currentServiceStageId', 'currentStageId', 'currentServiceStageEnteredAt', 'saleStatus', 'currentServiceStageExecutionTime', 'currentServiceStageSetupTime', 'currentServiceStageCommentsCount'])
    if (viewMode === 'service') {
      hiddenKeys.add('currentStageName')
    }
    return Object.entries(externalData)
      .filter(([key, value]) => !hiddenKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== '')
      .map(([key, value]) => ({
        key,
        label: labelMap[key] || key,
        value: String(value),
      }))
  }, [isModuleView, externalData, viewMode])

  return (
    <TicketItem
      ref={ref}
      onDoubleClick={onOpen}
      style={{ 
        opacity: isDragging ? 0.35 : (ticket.isActive === false ? 0.48 : 1),
        cursor: 'move',
        backgroundColor: statusVisual.backgroundColor,
        border: statusVisual.border
      }}
    >
      {coverImage && (
        <div style={{ width: '100%', height: '120px', marginBottom: '10px', overflow: 'hidden', borderRadius: '4px 4px 0 0' }}>
          <img 
            src={coverImage} 
            alt="Capa" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        </div>
      )}
      <div style={coverImage ? { padding: '0 12px' } : {}}>
        {ticket.urgency && (
          <HorizontalDiv>
            <MetaData color='#BF383B' fontSize='10px'><CircleAlertIcon size={16} /> URGENTE</MetaData>
          </HorizontalDiv>
        )}
      </div>
<HorizontalDiv>
  <MetaData>#{ticket.id}</MetaData>
</HorizontalDiv>
      {ticket.tags && ticket.tags.length > 0 &&
        <HorizontalDiv>
          {ticket.tags.filter(tag => tag.tag).map(tag => <TagComponent name={tag.tag.name} color={tag.tag.color} key={tag.tag.id} />)}
        </HorizontalDiv>
      }
      <TitleWithActions>
        {isEditing ? (
          <InputForAdd
            value={titleDraft}
            placeholder='Título do card'
            onChange={(e) => onChangeTitle(e.target.value)}
            onKeyDown={(e) => {
              if(e.key === 'Enter') onConfirmEdit()
              if(e.key === 'Escape') onCancelEdit()
            }}
            autoFocus
          />
        ) : (
          <CardTitle>{ticket.title}</CardTitle>
        )}
        {isEditing ? (
          <ActionGroup>
            <IconButton onClick={onConfirmEdit} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
            <IconButton onClick={onCancelEdit} onDoubleClick={(e) => e.stopPropagation()}><X size={16} /></IconButton>
          </ActionGroup>
        ) : (
          <ActionGroup>
            <CardActionButton
              ref={menuButtonRef}
              onClick={(e) => { e.stopPropagation(); handleToggleMenu() }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={18} />
            </CardActionButton>
          </ActionGroup>
        )}
  </TitleWithActions>
  {externalDataEntries.length > 0 && (
    <ExternalDataRow>
      {externalDataEntries.map(({ key, label, value }) => (
        <ExternalDataChip key={key}>
          <span style={{ fontWeight: 600, color: '#315f7f' }}>{label}:</span> {value}
        </ExternalDataChip>
      ))}
    </ExternalDataRow>
  )}
  {menuOpen && ReactDOM.createPortal(
        <CardActionMenu
          ref={menuRef}
          top={menuPosition.top}
          left={menuPosition.left}
          direction={menuPosition.direction}
        >
          <CardActionMenuItem onClick={() => { onOpen(); setMenuOpen(false) }}>
            <Eye size={16} /> Visualizar
          </CardActionMenuItem>
          {moduleType === 'product_development' && ticket.externalId && (
            <CardActionMenuItem onClick={() => {
              const currentPath = currentLocation.pathname + currentLocation.search
              navigate(`/panel/composition-product/edit/${ticket.externalId}?returnTo=${encodeURIComponent(currentPath)}`)
              setMenuOpen(false)
            }}>
              <ExternalLink size={16} /> Abrir produto
            </CardActionMenuItem>
          )}
          <CardActionMenuItem onClick={() => { onDuplicate(); setMenuOpen(false) }}>
            <Copy size={16} /> Duplicar
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onEditClick(); setMenuOpen(false) }}>
            <SquarePen size={16} /> Renomear
          </CardActionMenuItem>
          <CardActionMenuItem onClick={() => { onCopyLink(); setMenuOpen(false) }}>
            <Link2 size={16} /> Copiar link
          </CardActionMenuItem>
          {!showArchived && (
            <CardActionMenuItem onClick={() => { onToggleActive(); setMenuOpen(false) }}>
              {ticket.isActive === false ? <Check size={16} /> : <Circle size={16} />} {ticket.isActive === false ? 'Ativar' : 'Inativar'}
            </CardActionMenuItem>
          )}
          <CardActionMenuItem onClick={() => { onArchive(); setMenuOpen(false) }}>
            <Archive size={16} /> {showArchived ? 'Desarquivar' : 'Arquivar'}
          </CardActionMenuItem>
          <CardActionMenuItem $danger onClick={() => { onDelete(); setMenuOpen(false) }}>
            <Trash2 size={16} /> Excluir
          </CardActionMenuItem>
        </CardActionMenu>,
        document.body
      )}
      {coverFieldEntries.length > 0 && (
        <CoverFieldList>
          {coverFieldEntries.map((field) => (
            <CoverFieldItem key={field.id}>
              <CoverFieldIcon $integrated={field.integrated}>{field.icon}</CoverFieldIcon>
              <CoverFieldBody>
                <CoverFieldLabel>{field.label}</CoverFieldLabel>
                {(coverColorSwatchesByFieldId[field.id] || []).length > 0 && (
                  <CoverFieldSwatches>
                    {(coverColorSwatchesByFieldId[field.id] || []).map((hex, index) => (
                      <CoverFieldSwatch key={`${field.id}-${hex}-${index}`} $hex={hex} />
                    ))}
                  </CoverFieldSwatches>
                )}
                <CoverFieldValue $variant={field.variant}>{field.value}</CoverFieldValue>
              </CoverFieldBody>
            </CoverFieldItem>
          ))}
        </CoverFieldList>
      )}
      <CardMetaRow>
        {ticket.deadline && (
          <CardMetaItem
            color={isDeadlineExpired(ticket.deadline) ? '#BF383B' : undefined}
            style={isDeadlineExpired(ticket.deadline) ? { fontWeight: 700 } : undefined}
          >
            <Calendar size={14} />
            {formatDeadline(ticket.deadline)}
          </CardMetaItem>
        )}
        {currentStageExecutionTime != null && currentStageExecutionTime > 0 && (
          <CardMetaItem color="#4B5563">
            <Clock size={14} color="#10B981" />
            {formatElapsed(currentStageExecutionTime)}
          </CardMetaItem>
        )}
        {currentStageSetupTime != null && currentStageSetupTime > 0 && (
          <CardMetaItem color="#4B5563">
            <Clock size={14} color="#3B82F6" />
            {formatElapsed(currentStageSetupTime)}
          </CardMetaItem>
        )}
        {totalTasks > 0 && (
          <CardMetaItem>
            <CheckSquare size={14} />
            {completedTasks}/{totalTasks}
          </CardMetaItem>
        )}
        {commentsCount > 0 && (
          <CardMetaItem>
            <MessageSquareText size={14} />
            {commentsCount}
          </CardMetaItem>
        )}
        {attachmentsCount > 0 && (
          <CardMetaItem>
            <Paperclip size={14} />
            {attachmentsCount}
          </CardMetaItem>
        )}
      </CardMetaRow>
      {(ticket.createBy || stepElapsedSeconds != null) && (() => {
        const isStageTimeExpired = currentStageExecutionTime != null
          && currentStageExecutionTime > 0
          && stepElapsedSeconds != null
          && stepElapsedSeconds >= currentStageExecutionTime
        return (
          <CardFooter>
            {ticket.createBy ? (
              <CardCreatorAvatar title={ticket.createBy.name}>
                {ticket.createBy.picture
                  ? <img src={ticket.createBy.picture} alt={ticket.createBy.name} />
                  : getUserInitials(ticket.createBy.name)}
              </CardCreatorAvatar>
            ) : <span />}
            {stepElapsedSeconds != null && (
              <CardStageElapsedBadge $expired={isStageTimeExpired}>
                {formatStageElapsed(stepElapsedSeconds)}
              </CardStageElapsedBadge>
            )}
          </CardFooter>
        )
      })()}
      {isModuleView && moduleType === 'production' && externalData?.currentServiceStageId && !isLastStep && (
        <ProductionStageControls serviceStageId={externalData.currentServiceStageId} />
      )}
    </TicketItem>
  )
}

const DroppableStepColumn = ({ step, children, onTicketMove, onStepMove, onTicketReorderCommit, isStepReorderEnabled, isDragDisabled }: { 
  step: Step, 
  children: React.ReactNode,
  onTicketMove: (ticketId: number, newStepId: number) => void,
  onStepMove: (stepId: number, targetStepId: number) => void,
  onTicketReorderCommit: (stepId: number) => void,
  isStepReorderEnabled: boolean,
  isDragDisabled?: boolean
}) => {
  const ref = useRef<HTMLDivElement | null>(null)

  const [{ isOver }, drop] = useDrop<{ id: number; stepId?: number }, void, { isOver: boolean }>({
    accept: isStepReorderEnabled ? ['ticket', 'step'] : ['ticket'],
    canDrop: (item, monitor) => {
      const itemType = monitor.getItemType()
      if (itemType === 'step') return true
      return !isDragDisabled
    },
    drop: (item, monitor) => {
      const itemType = monitor.getItemType()
      if (itemType === 'ticket') {
        if (item.stepId !== step.id) {
          onTicketMove(item.id, step.id)
        } else {
          onTicketReorderCommit(step.id)
        }
      }
      if (isStepReorderEnabled && itemType === 'step' && item.id !== step.id) {
        onStepMove(item.id, step.id)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  const [{ isDraggingStep }, drag] = useDrag({
    type: 'step',
    canDrag: isStepReorderEnabled,
    item: { id: step.id },
    collect: (monitor) => ({
      isDraggingStep: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <StepColumn 
      ref={ref}
      style={{ 
        border: isOver ? '2px solid rgba(0, 144, 211, 0.3)' : 'none',
        opacity: isDraggingStep ? 0.6 : 1,
      }}
    >
      {children}
    </StepColumn>
  )
}

const DraggableStepFieldItem = ({
  stepId,
  pipelineFieldId,
  index,
  canDrag,
  onReorder,
  onCommit,
  children,
}: {
  stepId: number
  pipelineFieldId: number
  index: number
  canDrag: boolean
  onReorder: (stepId: number, dragIndex: number, hoverIndex: number) => void
  onCommit: (stepId: number) => void
  children: React.ReactNode
}) => {
  const ref = useRef<HTMLLIElement | null>(null)

  const [{ isDragging }, drag] = useDrag<StepFieldDragItem, void, { isDragging: boolean }>({
    type: 'step-field',
    canDrag,
    item: { stepId, pipelineFieldId, index },
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        onCommit(item.stepId)
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop<StepFieldDragItem, { stepId: number }, unknown>({
    accept: 'step-field',
    hover: (item, monitor) => {
      if (!ref.current) return
      if (item.stepId !== stepId) return
      if (item.pipelineFieldId === pipelineFieldId) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onReorder(stepId, dragIndex, hoverIndex)
      item.index = hoverIndex
    },
    drop: () => ({ stepId }),
  })

  drag(drop(ref))

  return (
    <TicketItem
      ref={ref}
      style={{
        opacity: isDragging ? 0.45 : 1,
        cursor: canDrag ? 'move' : 'default',
      }}
    >
      {children}
    </TicketItem>
  )
}

type FieldModalType = 
  | { mode: 'custom', stepId: number, field?: any }
  | { mode: 'integrated', stepId: number }
  | null

type ScrollableTicketListProps = React.ComponentProps<typeof TicketList> & {
  stepId: number
  onLoadMore: (element: HTMLElement, stepId: number) => void
}

const ScrollableTicketList = ({
  stepId,
  onLoadMore,
  ...props
}: ScrollableTicketListProps) => {
  const listRef = useRef<HTMLUListElement | null>(null)

  useDndScrolling(listRef, {
    ...ticketListScrollProps,
    onScrollChange: () => {
      if (listRef.current) {
        onLoadMore(listRef.current, stepId)
      }
    },
  })

  return <TicketList ref={listRef} {...props} />
}

const Kanban = () => {
  const { isConfiguringPipeline, viewOrEditTagsModalIsOpen, setViewOrEditTagsModalIsOpen, viewMode, sortBy, filters, setAvailableUsers, setAvailableTags, showArchived, showInactiveCards, setShowInactiveCards, isAddingTableCard, setIsAddingTableCard, contextPipelineId, isModuleView, moduleType, subModule } = usePipelineContext()
  const isProductionModuleView = isModuleView && moduleType === 'production'
  const isSalesModuleView = isModuleView && moduleType === 'sales'
  const isRestrictedModuleView = isProductionModuleView || isSalesModuleView
  const canModifySteps = !isRestrictedModuleView
  const outletContext = useOutletContext<TypeMainOutlet>()
  const globalSearch = typeof outletContext?.search === 'string' ? outletContext.search : ''
  const [ fieldModal, setFieldModal ] = useState<FieldModalType>(null)
  const [ fieldsInSteps, setFieldsInSteps ] = useState<StepFieldConfig[]>([])
  const [ addStepModalIsOpen, setAddStepModalIsOpen ] = useState<boolean>(false)
  const [ requiredFieldsModal, setRequiredFieldsModal ] = useState<{ ticketId: number, targetStepId: number } | null>(null)
  const navigate = useNavigate()
  const [ steps, setSteps ] = useState<Step[]>([])
  const [ tickets, setTickets ] = useState<Ticket[]>([])
  const [expiredCountByStep, setExpiredCountByStep] = useState<Record<number, number>>({})
  const [ticketExpiredMap, setTicketExpiredMap] = useState<Record<number, boolean>>({})
  const [ticketElapsedMap, setTicketElapsedMap] = useState<Record<number, number>>({})
  const [ticketExecutionTimeMap, setTicketExecutionTimeMap] = useState<Record<number, number | null>>({})
  const [expiredFilterStepId, setExpiredFilterStepId] = useState<number | null>(null)
  const [expiredFilterMode, setExpiredFilterMode] = useState<'expired' | 'non-expired' | null>(null)
  const [ allTags, setAllTags ] = useState<{id: number, name: string, color?: string}[]>([])
  const [ newTicketIn, setNewTicketIn ] = useState<number | null>(null)
  const [ newTicketTitle, setNewTicketTitle ] = useState<string>('')
  const [ creatingTicket, setCreatingTicket ] = useState<boolean>(false)
  const [ editingTicketId, setEditingTicketId ] = useState<number | null>(null)
  const [ ticketTitleDraft, setTicketTitleDraft ] = useState<string>('')
  const [ editingFieldId, setEditingFieldId ] = useState<number | null>(null)
  const [ fieldNameDraft, setFieldNameDraft ] = useState<string>('')
  const [ fieldRequiredDraft, setFieldRequiredDraft ] = useState<boolean>(false)
  const [ fieldShowOnCardDraft, setFieldShowOnCardDraft ] = useState<boolean>(false)
  const [ editingStepId, setEditingStepId ] = useState<number | null>(null)
  const [ stepNameDraft, setStepNameDraft ] = useState<string>('')
  const [ typeMap, setTypeMap ] = useState<Record<number, string>>({})
  const [ searchParams ] = useSearchParams()
  const ticketOrderRef = useRef<TicketOrderCache>({})
  const fieldOrderRef = useRef<Record<number, StepFieldConfig[]>>({})
  const fieldOrderRollbackRef = useRef<Record<number, StepFieldConfig[]>>({})
  const pipelineId = contextPipelineId ? String(contextPipelineId) : searchParams.get('pipelineId')
  const location = useLocation()
  const buildTicketRoute = useCallback((targetTicketId: number) => {
    const queryParams = new URLSearchParams({ ticketId: String(targetTicketId) })
    if (pipelineId && pipelineId !== 'null') {
      queryParams.set('pipelineId', pipelineId)
    }
    const currentPath = location.pathname + location.search
    queryParams.set('returnTo', currentPath)
    return `/panel/pipeline/ticket?${queryParams.toString()}`
  }, [pipelineId, location.pathname, location.search])
  const [ stepMenuOpen, setStepMenuOpen ] = useState<number | null>(null)
  const [ stepMenuPosition, setStepMenuPosition ] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const stepMenuButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
  const stepMenuRef = useRef<HTMLDivElement | null>(null)
  const integratedColorSwatchCacheRef = useRef<Record<string, string>>({})

  const TICKETS_PER_PAGE = 20
  const [visibleCountByStep, setVisibleCountByStep] = useState<Record<number, number>>({})

  const resolveIntegratedColorSwatches = useCallback(async ({ moduleId, fieldKey, names }: { moduleId?: string; fieldKey?: string; names: string[] }) => {
    if (!moduleId || !fieldKey || !Array.isArray(names) || names.length === 0) {
      return []
    }

    const normalizedFieldKey = String(fieldKey).trim()
    const normalizedNames = Array.from(new Set(names.map((name) => String(name || '').trim()).filter((name) => name.length > 0)))
    if (!normalizedNames.length) return []

    const buildCacheKey = (name: string) => `${moduleId}::${normalizedFieldKey}::${normalizeSearchText(name)}`
    const pendingNames = normalizedNames.filter((name) => !integratedColorSwatchCacheRef.current[buildCacheKey(name)])

    if (pendingNames.length > 0) {
      const moduleConfig = getModuleById(moduleId)
      if (moduleConfig) {
        try {
          const requestLimit = 600
          const paginationType = moduleConfig.paginationType || 'offset'
          const params = paginationType === 'page'
            ? { limit: requestLimit, page: 1 }
            : paginationType === 'skip-take'
            ? { skip: 0, take: requestLimit }
            : { limit: requestLimit, offset: 0 }

          const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}${moduleConfig.endpoint}`, { params })
          const records = resolveIntegrationResponseArray(response.data, moduleConfig.responseKey)

          records.forEach((record: any) => {
            const fieldValue = getNestedValueByPath(record, normalizedFieldKey)
            const valuesToProcess = Array.isArray(fieldValue)
              ? fieldValue
              : fieldValue && typeof fieldValue === 'object'
              ? [fieldValue]
              : []

            valuesToProcess.forEach((valueItem: any) => {
              const colorName = extractDisplayNameFromIntegrationItem(valueItem)
              const colorHex = extractSwatchHex(valueItem) || extractSwatchHex(record)
              if (!colorName || !colorHex) return
              const cacheKey = buildCacheKey(colorName)
              if (!integratedColorSwatchCacheRef.current[cacheKey]) {
                integratedColorSwatchCacheRef.current[cacheKey] = colorHex
              }
            })
          })
        } catch (error) {
          console.error('Erro ao resolver swatches de cores integradas da capa:', error)
        }
      }
    }

    return normalizedNames
      .map((name) => integratedColorSwatchCacheRef.current[buildCacheKey(name)])
      .filter((hex): hex is string => typeof hex === 'string' && hex.length > 0)
  }, [])

  useEffect(() => {
    const uniqueUsers = tickets
      .filter(t => t.createBy)
      .map(t => t.createBy!)
      .filter((user, index, self) => self.findIndex(u => u.id === user.id) === index)
    
    console.debug('[Kanban] Available users for filter:', uniqueUsers)
    setAvailableUsers(uniqueUsers)
  }, [tickets, setAvailableUsers])

  useEffect(() => {
    console.debug('[Kanban] Available tags for filter:', allTags)
    setAvailableTags(allTags)
  }, [allTags, setAvailableTags])

  const baseFilteredTickets = useMemo(() => {
    let result = [...tickets]
    const activeSearchTerms = [globalSearch, filters.keyword]
      .map((term) => normalizeSearchText(term))
      .filter((term) => term.length > 0)

    console.debug('[Kanban] Filtering tickets, initial count:', result.length)
    console.debug('[Kanban] Active filters:', filters)
    console.debug('[Kanban] Global search:', globalSearch)
    console.debug('[Kanban] Sort by:', sortBy)

    if (activeSearchTerms.length > 0) {
      result = result.filter((ticket) => {
        const stepName = steps.find((step) => step.id === ticket.stepId)?.name || ''
        const searchableContent = buildTicketSearchContent(ticket, stepName)
        return activeSearchTerms.every((term) => searchableContent.includes(term))
      })
    }

    if (filters.urgency) {
      result = result.filter(t => t.urgency)
    }

    const now = new Date()
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000)

    if (filters.dueDate.notExpired || filters.dueDate.nearExpiry || filters.dueDate.expired) {
      result = result.filter(t => {
        if (!t.deadline) return false
        const deadline = new Date(t.deadline)
        
        if (filters.dueDate.notExpired && deadline > now) return true
        if (filters.dueDate.nearExpiry && deadline > now && deadline <= threeDaysFromNow) return true
        if (filters.dueDate.expired && deadline < now) return true
        return false
      })
    }

    if (filters.dateFilter.from || filters.dateFilter.to) {
      const { field, from, to } = filters.dateFilter
      result = result.filter(t => isTicketInDateRange(t, field, from, to))
    }

    if (filters.assignedUsers.length > 0) {
      result = result.filter(t => t.createBy && filters.assignedUsers.includes(t.createBy.id))
    }

    if (filters.tags.length > 0) {
      result = result.filter(t => (t.tags ?? []).some(tag => filters.tags.includes(tag.tag.id)))
    }

    switch (sortBy) {
      case 'phase':
        result.sort((a, b) => {
          const stepA = steps.find(s => s.id === a.stepId)
          const stepB = steps.find(s => s.id === b.stepId)
          const nameA = stepA?.name || ''
          const nameB = stepB?.name || ''
          return nameA.localeCompare(nameB)
        })
        break
      case 'name_asc':
        result.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'name_desc':
        result.sort((a, b) => b.title.localeCompare(a.title))
        break
      case 'recent':
        result.sort((a, b) => b.id - a.id)
        break
      case 'oldest':
        result.sort((a, b) => a.id - b.id)
        break
      case 'urgency':
        result.sort((a, b) => (b.urgency ? 1 : 0) - (a.urgency ? 1 : 0))
        break
      case 'deadline':
        result.sort((a, b) => {
          if (!a.deadline && !b.deadline) return 0
          if (!a.deadline) return 1
          if (!b.deadline) return -1
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
        })
        break
    }

    return result
  }, [tickets, filters, sortBy, steps, globalSearch])

  const filteredAndSortedTickets = useMemo(() => {
    let result = baseFilteredTickets
    if (expiredFilterStepId !== null && expiredFilterMode !== null) {
      result = result.filter((t) => {
        if (t.stepId === expiredFilterStepId) {
          return expiredFilterMode === 'expired'
            ? ticketExpiredMap[t.id]
            : !ticketExpiredMap[t.id]
        }
        return true
      })
    }
    return result
  }, [baseFilteredTickets, expiredFilterStepId, expiredFilterMode, ticketExpiredMap])

  const visibleTicketsByStep = useMemo(() => {
    const grouped = filteredAndSortedTickets.reduce<Record<number, Ticket[]>>((accumulator, ticket) => {
      if (!accumulator[ticket.stepId]) {
        accumulator[ticket.stepId] = []
      }
      accumulator[ticket.stepId].push(ticket)
      return accumulator
    }, {})

    const paginated: Record<number, Ticket[]> = {}
    for (const stepId of Object.keys(grouped)) {
      const numericStepId = Number(stepId)
      const limit = visibleCountByStep[numericStepId] || TICKETS_PER_PAGE
      paginated[numericStepId] = grouped[numericStepId].slice(0, limit)
    }
    return paginated
  }, [filteredAndSortedTickets, visibleCountByStep])

  const totalTicketsByStep = useMemo(() => {
    return baseFilteredTickets.reduce<Record<number, number>>((acc, ticket) => {
      acc[ticket.stepId] = (acc[ticket.stepId] || 0) + 1
      return acc
    }, {})
  }, [baseFilteredTickets])

  const maybeLoadMoreTickets = useCallback((element: HTMLElement, stepId: number) => {
    const scrollThreshold = 50
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < scrollThreshold
    if (!isNearBottom) return

    const currentVisible = visibleCountByStep[stepId] || TICKETS_PER_PAGE
    const totalInStep = totalTicketsByStep[stepId] || 0
    if (currentVisible >= totalInStep) return

    setVisibleCountByStep(prev => ({
      ...prev,
      [stepId]: currentVisible + TICKETS_PER_PAGE,
    }))
  }, [visibleCountByStep, totalTicketsByStep])

  const handleColumnScroll = useCallback((stepId: number, event: React.UIEvent<HTMLUListElement>) => {
    maybeLoadMoreTickets(event.currentTarget, stepId)
  }, [maybeLoadMoreTickets])

  const fetchFieldsInStepsData = useCallback(async () => {
    if(!pipelineId) return
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?pipelineId=${pipelineId}`)
      if(response.data) {
        const normalized = response.data.data.map((f: any) => ({
          pipelineField: f.pipelineField ?? f.PipelineField,
          step: f.step ?? f.Step,
          position: typeof f.position === 'number' ? f.position : undefined,
          isRequired: f.isRequired,
          showOnCard: f.showOnCard,
        }))
        setFieldsInSteps(normalized)
      }
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  const getOrderedFieldsForStep = useCallback((stepId: number, sourceFields?: StepFieldConfig[]) => {
    const input = sourceFields || fieldsInSteps
    return input
      .filter((field) => field.step?.id === stepId)
      .sort((a, b) => {
        const positionA = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER
        const positionB = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER
        if (positionA === positionB) {
          return Number(a.pipelineField?.id || 0) - Number(b.pipelineField?.id || 0)
        }
        return positionA - positionB
      })
  }, [fieldsInSteps])

  useEffect(() => {
    const fetchFieldTypes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/types`)
        if(response.data?.data) {
          const map: Record<number, string> = {}
          response.data.data.forEach((item: {id:number, name:string}) => { map[item.id] = item.name })
          setTypeMap(map)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchFieldTypes()
  }, [])

  const fetchStepsAndTickets = useCallback(async () => {
    if(!pipelineId) return
    try {
      const ticketsRequest = showArchived
        ? axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket?pipelineId=${pipelineId}&archived=true`)
        : axios.get(
            `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket?pipelineId=${pipelineId}&archived=false&includeInactive=${showInactiveCards && viewMode === 'board'}`,
          )

      const [responseSteps, responseTickets, responseTags] = await Promise.all([
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`),
        ticketsRequest,
        axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/tag`)
      ])
      let stepsData: any[] = []
      if (responseSteps.data.data) {
        stepsData = [...responseSteps.data.data];
        if (moduleType === 'production' && (viewMode === 'sector' || viewMode === 'service')) {
          const savedOrderStr = localStorage.getItem(`production-steps-order:${subModule || 'steel'}:${viewMode}`);
          if (savedOrderStr) {
            try {
              const savedOrder: string[] = JSON.parse(savedOrderStr);
              const orderMap = new Map<string, number>(savedOrder.map((name, index) => [name, index]));
              stepsData.sort((a: any, b: any) => {
                const hasA = orderMap.has(a.name);
                const hasB = orderMap.has(b.name);
                if (hasA && hasB) {
                  return orderMap.get(a.name)! - orderMap.get(b.name)!;
                }
                if (hasA) return -1;
                if (hasB) return 1;
                return (a.position ?? 0) - (b.position ?? 0);
              });
            } catch (e) {
              console.error(e);
}
}
}
}
setSteps(stepsData);
      if(responseTickets.data.data) {
        const normalized = responseTickets.data.data.map((t: any) => {
          const mapped = { ...t };
          if (!mapped.coverFile && mapped.GeneralFile) {
            mapped.coverFile = {
              id: mapped.GeneralFile.id,
              fileUrl: mapped.GeneralFile.fileUrl,
              title: mapped.GeneralFile.title,
              originalName: mapped.GeneralFile.originalName,
              fileName: mapped.GeneralFile.fileName,
              fileSize: mapped.GeneralFile.fileSize,
              mimeType: mapped.GeneralFile.mimeType
            }
          }
          mapped.comments = mapped.Comment || mapped.comments || mapped.comments === undefined ? (mapped.Comment ? mapped.Comment.map((c: any) => ({ id: c.id })) : (mapped.comments || [])) : mapped.comments
          const rawTags = mapped.TagTicket || mapped.tags || []
          mapped.tags = rawTags.map((t: any) => {
            if (t.tag) return t
            if (t.Tag) return { ...t, tag: t.Tag }
            return t
          })
          mapped.isActive = mapped.isActive !== false
          mapped.archived = Boolean(mapped.archived)
          mapped.createBy = mapped.User || mapped.createBy
          mapped.tasks = mapped.TicketTask || mapped.tasks || []
          mapped.fileInTickets = mapped.FileInTicket || mapped.fileInTickets || []
          if (mapped.FieldInTicket) {
            mapped.fieldInTickets = mapped.FieldInTicket.map((fit: any) => ({
              pipelineFieldId: fit.pipelineFieldId,
              stringValue: fit.stringValue,
              bigStringValue: fit.bigStringValue,
              integerValue: fit.integerValue,
              decimalValue: fit.decimalValue,
              dateTimeValue: fit.dateTimeValue,
              booleanValue: fit.booleanValue,
              pipelineField: {
                id: fit.PipelineField?.id,
                name: fit.PipelineField?.name,
                typeFieldId: fit.PipelineField?.typeFieldId,
                typeField: fit.PipelineField?.TypeField,
                integrationConfig: fit.PipelineField?.integrationConfig,
              },
              isRequired: false,
            }))
          }
          return mapped
        })
        setTickets(normalized)
        ;(async () => {
          try {
            const stepById: Record<number, any> = {}
            for (const s of stepsData) stepById[s.id] = s

            const expiredMap: Record<number, boolean> = {}
            const expiredCounts: Record<number, number> = {}
            const elapsedMap: Record<number, number> = {}
            const execTimeMap: Record<number, number | null> = {}

            const ticketIds = normalized.map((tk: any) => tk.id)

            const elapsedRes = await axios.post(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/step-history/elapsed-batch`,
              { ticketIds },
            )
            const rawElapsed = elapsedRes.data?.data ?? elapsedRes.data ?? {}
            const elapsedFromHistory: Record<number, number> = {}
            if (rawElapsed && typeof rawElapsed === 'object' && !Array.isArray(rawElapsed)) {
              for (const [key, value] of Object.entries(rawElapsed)) {
                const ticketId = Number(key)
                if (!Number.isNaN(ticketId) && typeof value === 'number') {
                  elapsedFromHistory[ticketId] = value
                }
              }
            }

            for (const tk of normalized) {
              const stepExecTime = tk.externalData?.currentServiceStageExecutionTime != null
                ? Number(tk.externalData.currentServiceStageExecutionTime)
                : null
              const elapsed = elapsedFromHistory[tk.id] ?? 0
              const isExpired = stepExecTime != null && stepExecTime > 0 && elapsed >= stepExecTime

              expiredMap[tk.id] = isExpired
              elapsedMap[tk.id] = elapsed
              execTimeMap[tk.id] = stepExecTime
              const stepId = tk.stepId
              if (!expiredCounts[stepId]) expiredCounts[stepId] = 0
              if (isExpired) expiredCounts[stepId] += 1
            }

            setTicketExpiredMap(expiredMap)
            setExpiredCountByStep(expiredCounts)
            setTicketElapsedMap(elapsedMap)
            setTicketExecutionTimeMap(execTimeMap)
          } catch (e) {
            console.error('[Kanban] erro ao calcular expirados', e)
          }
        })()
      }
      if(responseTags.data?.data) {
        console.debug('[Kanban] Fetched tags:', responseTags.data.data)
        setAllTags(responseTags.data.data)
      }
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId, showArchived, showInactiveCards, viewMode])

  const saveStepOrder = useCallback(async (orderedSteps: Step[]) => {
    if(!pipelineId) return
    try {
      await Promise.all(
        orderedSteps.map((step, idx) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${step.id}` , {
            position: idx,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [pipelineId])

  const saveTicketOrder = useCallback(async (stepId: number, ticketsInStep: Ticket[]) => {
    try {
      if (!ticketsInStep.length) return
      const orderedTickets = [...ticketsInStep].sort((a, b) => {
        const positionA = typeof a.position === 'number' ? a.position : 0
        const positionB = typeof b.position === 'number' ? b.position : 0
        if (positionA === positionB) return a.id - b.id
        return positionA - positionB
      })
      await Promise.all(
        orderedTickets.map((ticket, index) =>
          axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticket.id}`, {
            stepId,
            position: index,
          })
        )
      )
    } catch (error) {
      console.error(error)
    }
  }, [])

  const saveFieldOrder = useCallback(async (stepId: number, orderedFields: StepFieldConfig[]) => {
    if (!orderedFields.length) return
    await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/fields/reorder`, {
      fieldIds: orderedFields.map((field) => Number(field.pipelineField?.id)).filter((id) => Number.isFinite(id)),
    })
  }, [])

  const handleTicketReorderInStep = useCallback((stepId: number, dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return

    setTickets((prev) => {
      const ticketsInStep = prev.filter((ticket) => ticket.stepId === stepId)
      if (!ticketsInStep.length) return prev

      const orderedTicketsInStep = [...ticketsInStep].sort((a, b) => {
        const aPos = typeof a.position === 'number' ? a.position : 0
        const bPos = typeof b.position === 'number' ? b.position : 0
        if (aPos === bPos) return a.id - b.id
        return aPos - bPos
      })

      if (
        dragIndex < 0 ||
        hoverIndex < 0 ||
        dragIndex >= orderedTicketsInStep.length ||
        hoverIndex >= orderedTicketsInStep.length
      ) {
        return prev
      }

      const reorderedTickets = [...orderedTicketsInStep]
      const [removedTicket] = reorderedTickets.splice(dragIndex, 1)
      reorderedTickets.splice(hoverIndex, 0, removedTicket)

      const normalizedReorderedTickets = reorderedTickets.map((ticket, index) => ({
        ...ticket,
        position: index,
      }))

      ticketOrderRef.current[stepId] = normalizedReorderedTickets

      const reorderedIds = new Set(normalizedReorderedTickets.map((ticket) => ticket.id))
      const untouchedTickets = prev.filter((ticket) => !reorderedIds.has(ticket.id))

      return [...untouchedTickets, ...normalizedReorderedTickets]
    })
  }, [])

  const handleFieldReorderInStep = useCallback((stepId: number, dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return

    setFieldsInSteps((previousFields) => {
      const orderedStepFields = getOrderedFieldsForStep(stepId, previousFields)
      if (!orderedStepFields.length) return previousFields

      if (
        dragIndex < 0 ||
        hoverIndex < 0 ||
        dragIndex >= orderedStepFields.length ||
        hoverIndex >= orderedStepFields.length
      ) {
        return previousFields
      }

      if (!fieldOrderRollbackRef.current[stepId]) {
        fieldOrderRollbackRef.current[stepId] = orderedStepFields.map((field) => ({ ...field }))
      }

      const reorderedFields = [...orderedStepFields]
      const [removedField] = reorderedFields.splice(dragIndex, 1)
      reorderedFields.splice(hoverIndex, 0, removedField)

      const normalizedReorderedFields = reorderedFields.map((field, index) => ({
        ...field,
        position: index,
      }))

      fieldOrderRef.current[stepId] = normalizedReorderedFields

      const movedFieldIds = new Set(normalizedReorderedFields.map((field) => Number(field.pipelineField?.id)))
      const untouchedFields = previousFields.filter(
        (field) => field.step?.id !== stepId || !movedFieldIds.has(Number(field.pipelineField?.id)),
      )

      return [...untouchedFields, ...normalizedReorderedFields]
    })
  }, [getOrderedFieldsForStep])

  const handleTicketReorderCommit = useCallback(async (stepId: number) => {
    const reorderedTickets = ticketOrderRef.current[stepId]
    if (!reorderedTickets || !reorderedTickets.length) return

    try {
      await saveTicketOrder(stepId, reorderedTickets)
      delete ticketOrderRef.current[stepId]
    } catch (error) {
      console.error(error)
    }
  }, [saveTicketOrder])

  const handleFieldReorderCommit = useCallback(async (stepId: number) => {
    const reorderedFields = fieldOrderRef.current[stepId]
    if (!reorderedFields || !reorderedFields.length) return

    try {
      await saveFieldOrder(stepId, reorderedFields)
      delete fieldOrderRef.current[stepId]
      delete fieldOrderRollbackRef.current[stepId]
    } catch (error) {
      console.error(error)
      const rollbackFields = fieldOrderRollbackRef.current[stepId]
      if (rollbackFields?.length) {
        setFieldsInSteps((previousFields) => {
          const rollbackIds = new Set(rollbackFields.map((field) => Number(field.pipelineField?.id)))
          const untouchedFields = previousFields.filter(
            (field) => field.step?.id !== stepId || !rollbackIds.has(Number(field.pipelineField?.id)),
          )
          return [...untouchedFields, ...rollbackFields]
        })
      } else {
        fetchFieldsInStepsData()
      }

      delete fieldOrderRef.current[stepId]
      delete fieldOrderRollbackRef.current[stepId]
      alert('Nao foi possivel salvar a ordem dos campos. A ordem anterior foi restaurada.')
    }
  }, [fetchFieldsInStepsData, saveFieldOrder])

  useEffect(() => {
    if(!isConfiguringPipeline) {
      setEditingStepId(null)
      setStepNameDraft('')
    }
  }, [isConfiguringPipeline])

  useEffect(() => {
    fetchFieldsInStepsData()
  }, [fetchFieldsInStepsData])

  useEffect(() => {
    fetchStepsAndTickets()
  }, [fetchStepsAndTickets])

  useEffect(() => {
    if(!addStepModalIsOpen) {
      fetchStepsAndTickets()
    }
  }, [addStepModalIsOpen, fetchStepsAndTickets])

  useEffect(() => {
    const handleSyncComplete = () => {
      setVisibleCountByStep({})
      fetchStepsAndTickets()
      fetchFieldsInStepsData()
    }
    window.addEventListener('pipeline-sync-complete', handleSyncComplete)
    return () => window.removeEventListener('pipeline-sync-complete', handleSyncComplete)
  }, [fetchStepsAndTickets, fetchFieldsInStepsData])

  const createTicket = async () => {
    if(creatingTicket) return
    const trimmed = newTicketTitle.trim()
    if(!trimmed || newTicketIn === null) {
      setNewTicketIn(null)
      setNewTicketTitle('')
      return
    }
    try {
      setCreatingTicket(true)
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: trimmed,
        stepId: newTicketIn
      })
      if(response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[]}])
      }
    } catch (error) {
      console.error(error)
    } finally {
      setCreatingTicket(false)
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleCreateTicket = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await createTicket()
    }
    if(e.key === 'Escape') {
      setNewTicketIn(null)
      setNewTicketTitle('')
    }
  }

  const handleUpdateStep = async (stepId: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if(e.key === 'Enter') {
      await saveStepName(stepId)
    }
    if(e.key === 'Escape') {
      cancelEditStep()
    }
  }

  const saveStepName = async (stepId: number) => {
    const newName = stepNameDraft.trim()
    if(!newName) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`, {
        name: newName
      })
      if(response.data) {
        setSteps(prev => prev.map(step => step.id === stepId ? response.data.data : step))
        setEditingStepId(null)
        setStepNameDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditStep = () => {
    setEditingStepId(null)
    setStepNameDraft('')
  }

  const handleDeleteStep = async (stepId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`)
      setSteps(prev => prev.filter(step => step.id !== stepId))
      setTickets(prev => prev.filter(ticket => ticket.stepId !== stepId))
      setFieldsInSteps(prev => prev.filter(field => field.step.id !== stepId))
      if(editingStepId === stepId) {
        cancelEditStep()
      }
      setStepMenuOpen(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDuplicateStep = async (stepId: number) => {
    try {
      const step = steps.find(s => s.id === stepId)
      if (!step) return


      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step`, {
        name: `${step.name} (cópia)`,
        pipelineId: Number(pipelineId)
      })

      if (response.data) {
        const newStep = response.data.data
        setSteps(prev => [...prev, newStep])


        const ticketsToDuplicate = tickets.filter(t => t.stepId === stepId)
        for (const ticket of ticketsToDuplicate) {
          const duplicateResponse = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticket.id}/duplicate`, {
            stepId: newStep.id
          })
          if (duplicateResponse.data) {
            setTickets(prev => [...prev, duplicateResponse.data.data])
          }
        }
      }
      setStepMenuOpen(null)
    } catch (error) {
      console.error(error)
    }
  }

  const handleToggleStepMenu = (stepId: number) => {
    if (stepMenuOpen === stepId) {
      setStepMenuOpen(null)
      return
    }

    const button = stepMenuButtonRefs.current[stepId]
    if (button) {
      const rect = button.getBoundingClientRect()
      setStepMenuPosition({ top: rect.bottom + 5, left: rect.left })
    }
    setStepMenuOpen(stepId)
  }

  const handleToggleStepLock = async (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    if (!step) return
    
    const newValue = !(step as any).allowCardCreation
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}`, {
        allowCardCreation: newValue
      })
      setSteps(prev => prev.map(s => s.id === stepId ? { ...s, allowCardCreation: newValue } : s))
    } catch (error) {
      console.error('Erro ao atualizar configuração da etapa:', error)
    }
  }

  const handleToggleShowInactiveCards = () => {
    setShowInactiveCards((previousValue) => !previousValue)
  }

  const handleStartRenameStep = (stepId: number) => {
    const step = steps.find(s => s.id === stepId)
    if (step) {
      setEditingStepId(stepId)
      setStepNameDraft(step.name)
    }
    setStepMenuOpen(null)
  }


  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (stepMenuRef.current && !stepMenuRef.current.contains(e.target as Node)) {
        const isButtonClick = Object.values(stepMenuButtonRefs.current).some(btn => btn && btn.contains(e.target as Node))
        if (!isButtonClick) {
          setStepMenuOpen(null)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDeleteField = async (stepId: number, fieldId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field/${fieldId}`)
      setFieldsInSteps(prev => prev.filter(field => !(field.pipelineField.id === fieldId && field.step.id === stepId)))
      fetchFieldsInStepsData()
    } catch (error) {
      console.error(error)
    }
  }

  const moveStep = (sourceStepId: number, targetStepId: number) => {
    if (!isConfiguringPipeline) return
    const sourceIndex = steps.findIndex(step => step.id === sourceStepId)
    const targetIndex = steps.findIndex(step => step.id === targetStepId)
    if(sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return
    const updated = [...steps]
    const [removed] = updated.splice(sourceIndex, 1)
    updated.splice(targetIndex, 0, removed)
    const positioned = updated.map((step, idx) => ({ ...step, position: idx }))
    setSteps(positioned)
    if (moduleType === 'production' && (viewMode === 'sector' || viewMode === 'service')) {
      const stepNames = positioned.map(s => s.name);
      localStorage.setItem(`production-steps-order:${subModule || 'steel'}:${viewMode}`, JSON.stringify(stepNames));
    }
    saveStepOrder(positioned)
  }


const handleTicketMove = async (ticketId: number, newStepId: number) => {
    const sourceTicket = tickets.find((t) => t.id === ticketId)
    const sourceStepId = sourceTicket?.stepId
    const targetStep = steps.find((s) => s.id === newStepId)
    const sourceStep = steps.find((s) => s.id === sourceStepId)

    if (sourceStepId !== newStepId && targetStep?.allowCardCreation === false) {
      alert('Não é possível mover card para esta etapa porque a adição de cards está desabilitada.')
      return
    }

    if (targetStep?.allowManualMove === false) {
      alert('Não é possível mover cards manualmente para esta etapa.')
      return
    }

    if (sourceStep?.sequentialFlow === true && sourceStepId !== newStepId) {
      const orderedSteps = [...steps].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      const sourceIndex = orderedSteps.findIndex(s => s.id === sourceStepId)
      const targetIndex = orderedSteps.findIndex(s => s.id === newStepId)
      if (sourceIndex !== -1 && targetIndex !== -1 && targetIndex !== sourceIndex + 1) {
        alert('O fluxo sequencial exige que o card seja movido apenas para a próxima etapa.')
        return
      }
    }

    try {
      const requiredFieldsResponse = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/required-fields/${newStepId}`
      )

      if (requiredFieldsResponse.data?.data && !requiredFieldsResponse.data.data.allFilled) {
        setRequiredFieldsModal({ ticketId, targetStepId: newStepId })
        return
      }

      const targetTickets = tickets.filter((t) => t.stepId === newStepId && t.id !== ticketId)
      const newPosition = targetTickets.length
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        stepId: newStepId,
      })
      if(response.data) {
        setTickets(prev => {
          const moved = prev.find((t) => t.id === ticketId)
          const withoutMoved = prev.filter((t) => t.id !== ticketId)
          const targetList = withoutMoved.filter((t) => t.stepId === newStepId)
          const sourceList = sourceStepId ? withoutMoved.filter((t) => t.stepId === sourceStepId) : []
          const newTarget = [...targetList, { ...(moved || response.data.data), stepId: newStepId, position: newPosition }]
          const reorderedTarget = newTarget.map((t, idx) => ({ ...t, position: idx }))
          const reorderedSource = sourceStepId && sourceStepId !== newStepId
            ? sourceList.map((t, idx) => ({ ...t, position: idx }))
            : sourceList
          const others = withoutMoved.filter((t) => t.stepId !== newStepId && (!sourceStepId || t.stepId !== sourceStepId))
          if (sourceStepId && sourceStepId !== newStepId && reorderedSource.length) {
            saveTicketOrder(sourceStepId, reorderedSource)
          }
          saveTicketOrder(newStepId, reorderedTarget)
          return [...others, ...reorderedSource, ...reorderedTarget]
        })

        setTimeout(() => {
          fetchStepsAndTickets()
        }, 250)

        setTimeout(() => {
          fetchStepsAndTickets()
        }, 1200)
      }
    } catch (error) {
      console.error('Erro ao mover ticket:', error)
    }
  }

  const saveTicketTitle = async (ticketId: number) => {
    const newTitle = ticketTitleDraft.trim()
    if(!newTitle) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: newTitle
      })
      if(response.data) {
        setTickets(prev => prev.map(ticket => ticket.id === ticketId ? {...ticket, title: response.data.data.title} : ticket))
        setEditingTicketId(null)
        setTicketTitleDraft('')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditTicket = () => {
    setEditingTicketId(null)
    setTicketTitleDraft('')
  }

  const saveFieldName = async (stepId: number, fieldId: number) => {
    const trimmed = fieldNameDraft.trim()
    if(!trimmed) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field/${fieldId}`, {
        name: trimmed
      })

      try {
        await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field/${fieldId}`, {
          isRequired: fieldRequiredDraft,
          showOnCard: fieldShowOnCardDraft,
        })
      } catch (requiredError) {
        console.error(requiredError)
      }

      if(response.data) {
        setFieldsInSteps((prev) => prev.map((item) => {
          if (item.pipelineField.id !== fieldId || item.step.id !== stepId) return item

          const nextIntegrationConfig = item.pipelineField.integrationConfig
            ? { ...item.pipelineField.integrationConfig, isRequired: fieldRequiredDraft }
            : item.pipelineField.integrationConfig

          return {
            ...item,
            isRequired: fieldRequiredDraft,
            showOnCard: fieldShowOnCardDraft,
            pipelineField: {
              ...item.pipelineField,
              name: response.data.data.name,
              integrationConfig: nextIntegrationConfig,
            },
          }
        }))
        setEditingFieldId(null)
        setFieldNameDraft('')
        setFieldRequiredDraft(false)
        setFieldShowOnCardDraft(false)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const cancelEditField = () => {
    setEditingFieldId(null)
    setFieldNameDraft('')
    setFieldRequiredDraft(false)
    setFieldShowOnCardDraft(false)
  }

  const handleDeleteTicket = async (ticketId: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`)
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketId))
      if(editingTicketId === ticketId) {
        cancelEditTicket()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleDuplicateTicket = async (ticketId: number) => {
    const ticket = tickets.find(t => t.id === ticketId)
    if (!ticket) return
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title: `${ticket.title} (cópia)`,
        stepId: ticket.stepId
      })
      if (response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[]}])
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleCopyLink = (ticketId: number) => {
    const url = `${window.location.origin}${buildTicketRoute(ticketId)}`
    navigator.clipboard.writeText(url)
    alert('Link copiado para a área de transferência')
  }

  const handleArchiveTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/archive`)
      setTickets((previousTickets) => {
        if (showArchived) {
          return previousTickets.map((ticket) =>
            ticket.id === ticketId ? { ...ticket, archived: true } : ticket,
          )
        }
        return previousTickets.filter((ticket) => ticket.id !== ticketId)
      })
    } catch (error) {
      console.error('Erro ao arquivar ticket:', error)
    }
  }

  const handleUnarchiveTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/unarchive`)
      setTickets((previousTickets) => {
        if (showArchived) {
          return previousTickets.filter((ticket) => ticket.id !== ticketId)
        }
        return previousTickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, archived: false } : ticket,
        )
      })
    } catch (error) {
      console.error('Erro ao desarquivar ticket:', error)
    }
  }

  const handleDeactivateTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/deactivate`)
      setTickets((previousTickets) => {
        if (showInactiveCards && !showArchived) {
          return previousTickets.map((ticket) =>
            ticket.id === ticketId ? { ...ticket, isActive: false } : ticket,
          )
        }
        return previousTickets.filter((ticket) => ticket.id !== ticketId)
      })
    } catch (error) {
      console.error('Erro ao inativar ticket:', error)
    }
  }

  const handleActivateTicket = async (ticketId: number) => {
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/activate`)
      setTickets((previousTickets) =>
        previousTickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, isActive: true } : ticket,
        ),
      )
    } catch (error) {
      console.error('Erro ao ativar ticket:', error)
    }
  }

  const handleToggleSaleStatus = async (ticketId: number) => {
    const ticket = tickets.find(t => t.id === ticketId)
    if (!ticket) return
    const nextStatus = ticket.saleStatus === 'closed' ? 'open' : 'closed'
    try {
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        saleStatus: nextStatus,
      })
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, saleStatus: nextStatus } : t))
    } catch (error) {
      console.error('Erro ao alterar status de venda:', error)
    }
  }

  const handleAdvanceStep = async (ticketId: number) => {
    const ticket = tickets.find(t => t.id === ticketId)
    if (!ticket) return
    const currentStep = steps.find(s => s.id === ticket.stepId)
    if (!currentStep) return
    const orderedSteps = [...steps].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    const currentIndex = orderedSteps.findIndex(s => s.id === ticket.stepId)
    if (currentIndex === -1 || currentIndex >= orderedSteps.length - 1) return
    const nextStep = orderedSteps[currentIndex + 1]
    await handleTicketMove(ticketId, nextStep.id)
  }

  const handleRenameFromTable = (ticketId: number) => {
    setEditingTicketId(ticketId)
    const ticket = tickets.find(t => t.id === ticketId)
    if (ticket) {
      setTicketTitleDraft(ticket.title)
    }
  }

  const handleCreateTableTicket = async (title: string, stepId: number) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket`, {
        title,
        stepId
      })
      if(response.data) {
        setTickets(prev => [...prev, {...response.data.data, comments:[], tags:[], tasks:[], fileInTickets:[]}])
      }
    } catch (error) {
      console.error(error)
    }
  }

  const orderedStepsForLastCheck = useMemo(() =>
    [...steps].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  [steps])

  const isLastStepInPipeline = useCallback((stepId: number): boolean => {
    if (!orderedStepsForLastCheck.length) return false
    return orderedStepsForLastCheck[orderedStepsForLastCheck.length - 1].id === stepId
  }, [orderedStepsForLastCheck])

  if (viewMode === 'table') {
    return (
      <>
        <TableView
          tickets={filteredAndSortedTickets.map(t => ({ ...t, comments: t.comments ?? [], tags: t.tags ?? [], tasks: t.tasks ?? [], fileInTickets: t.fileInTickets ?? [] }))}
          steps={steps}
          onView={(id) => navigate(buildTicketRoute(id))}
          onDuplicate={handleDuplicateTicket}
          onRename={handleRenameFromTable}
          onCopyLink={handleCopyLink}
          onArchive={showArchived ? handleUnarchiveTicket : handleArchiveTicket}
          onDelete={handleDeleteTicket}
          showArchived={showArchived}
          isAddingCard={isAddingTableCard}
          onCancelAddCard={() => setIsAddingTableCard(false)}
          onCreate={handleCreateTableTicket}
          editingTicketId={editingTicketId}
          titleDraft={ticketTitleDraft}
          onTitleDraftChange={setTicketTitleDraft}
          onSaveTitle={saveTicketTitle}
          onCancelEdit={cancelEditTicket}
        />
        {viewOrEditTagsModalIsOpen && <ViewOrEditTags closeModal={() => setViewOrEditTagsModalIsOpen(false)} />}
      </>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <ScrollingPipeline {...pipelineScrollProps}>
        {steps.map(step => {
          const visibleTicketsInStep = visibleTicketsByStep[step.id] || []
          const visibleCardCount = visibleTicketsInStep.length
          const totalCardCount = totalTicketsByStep[step.id] || 0

          return (
          <DroppableStepColumn
            key={step.id}
            step={step}
            onTicketMove={handleTicketMove}
            onStepMove={moveStep}
            onTicketReorderCommit={handleTicketReorderCommit}
            isStepReorderEnabled={isConfiguringPipeline}
            isDragDisabled={isProductionModuleView}
          >
            <StepHeader>
              <TitleWithActions>
                {editingStepId === step.id && canModifySteps ? (
                  <InputForAdd
                    value={stepNameDraft}
                    placeholder='Nome da etapa'
                    onChange={(e) => setStepNameDraft(e.target.value)}
                    onKeyDown={(e) => handleUpdateStep(step.id, e)}
                    autoFocus
                  />
                ) : (
                  <CardTitle>{step.name}</CardTitle>
                )}
                {editingStepId === step.id ? (
                  <ActionGroup>
                    <IconButton onClick={() => saveStepName(step.id)}><Check size={16} /></IconButton>
                    <IconButton onClick={cancelEditStep}><X size={16} /></IconButton>
                  </ActionGroup>
                ) : (
                  <ActionGroup>
                    <StepCardCountBadge
                      title='Tickets não expirados nesta etapa'
                      $active={expiredFilterStepId === step.id && expiredFilterMode === 'non-expired'}
                      onClick={() => {
                        if (expiredFilterStepId === step.id && expiredFilterMode === 'non-expired') {
                          setExpiredFilterStepId(null)
                          setExpiredFilterMode(null)
                        } else {
                          setExpiredFilterStepId(step.id)
                          setExpiredFilterMode('non-expired')
                        }
                      }}
                    >
                      {totalCardCount - (expiredCountByStep[step.id] || 0)}
                    </StepCardCountBadge>
                    { (expiredCountByStep[step.id] || 0) > 0 && (
                      <>
                        <StepExpiredBadge
                          title='Tickets expirados nesta etapa'
                          $active={expiredFilterStepId === step.id && expiredFilterMode === 'expired'}
                          onClick={() => {
                            if (expiredFilterStepId === step.id && expiredFilterMode === 'expired') {
                              setExpiredFilterStepId(null)
                              setExpiredFilterMode(null)
                            } else {
                              setExpiredFilterStepId(step.id)
                              setExpiredFilterMode('expired')
                            }
                          }}
                        >
                          {expiredCountByStep[step.id]}
                        </StepExpiredBadge>
                      </>
                    )}
                    {!isConfiguringPipeline && (step as any).allowCardCreation !== false ? (
                      <StepAddCardButton onClick={() => setNewTicketIn(step.id)} title='Adicionar card'>
                        <Plus size={16} />
                      </StepAddCardButton>
                    ) : null}
                    {isConfiguringPipeline && canModifySteps ? (
                      <StepSettingsButton 
                        ref={el => stepMenuButtonRefs.current[step.id] = el}
                        onClick={() => handleToggleStepMenu(step.id)}
                      >
                        <Settings size={16} />
                      </StepSettingsButton>
                    ) : null}
                  </ActionGroup>
                )}
              </TitleWithActions>
            </StepHeader>
            <ScrollableTicketList
              stepId={step.id}
              onLoadMore={maybeLoadMoreTickets}
              onScroll={(e: React.UIEvent<HTMLUListElement>) => handleColumnScroll(step.id, e)}
            >
              {isConfiguringPipeline ? <>
                <AddInStep onClick={() => setFieldModal({ mode: 'integrated', stepId: step.id })}><Plus />Adicionar campo</AddInStep>
                {getOrderedFieldsForStep(step.id).map((field, fieldIndex) => {
                  const isEditingField = editingFieldId === field.pipelineField.id
                  const isIntegrated = !!field.pipelineField.integrationConfig
                  const typeId = field.pipelineField.typeField?.id || (field.pipelineField as any).typeFieldId
                  
                  let typeLabel = ''
                  if (isIntegrated) {
                    const integrationType = field.pipelineField.integrationConfig?.integrationType === 'write' ? 'registro' : 'leitura'
                    const isRequired = field.isRequired || field.pipelineField.integrationConfig?.isRequired
                    const booleanFilterValue = field.pipelineField.integrationConfig?.booleanFilterValue
                    const booleanLegend = typeof booleanFilterValue === 'boolean'
                      ? ` - ${booleanFilterValue ? 'Ativo' : 'Inativo'}`
                      : ''
                    typeLabel = `integrado - tipo ${integrationType}${booleanLegend}${isRequired ? ' - obrigatório' : ''}${field.showOnCard ? ' - capa' : ''}`
                  } else {
                    const typeName = field.pipelineField.typeField?.name || (typeId ? typeMap[typeId] : '') || ''
                    typeLabel = typeName + (field.isRequired ? ' - obrigatório' : '') + (field.showOnCard ? ' - capa' : '')
}

return (
                    <DraggableStepFieldItem
                      key={`${step.id}-${field.pipelineField.id}`}
                      stepId={step.id}
                      pipelineFieldId={field.pipelineField.id}
                      index={fieldIndex}
                      canDrag={!isEditingField}
                      onReorder={handleFieldReorderInStep}
                      onCommit={handleFieldReorderCommit}
                    >
                      <TitleWithActions>
                        {isEditingField ? (
                          <InputForAdd
                            value={fieldNameDraft}
                            placeholder='Nome do campo'
                            onChange={(e) => setFieldNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if(e.key === 'Enter') saveFieldName(step.id, field.pipelineField.id)
                              if(e.key === 'Escape') cancelEditField()
                            }}
                            autoFocus
                          />
                        ) : (
                          <CardTitle>
                            {field.pipelineField.name} {typeLabel ? <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>({typeLabel})</span> : null}
                          </CardTitle>
                        )}
                        <ActionGroup>
                          {isEditingField ? (
                            <>
                              <IconButton onClick={() => saveFieldName(step.id, field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Check size={16} /></IconButton>
                              <RequiredCheckboxButton
                                onClick={() => setFieldRequiredDraft((prev) => !prev)}
                                onDoubleClick={(e) => e.stopPropagation()}
                                title={fieldRequiredDraft ? 'Campo obrigatório' : 'Campo não obrigatório'}
                              >
                                <RequiredCheckboxMark $active={fieldRequiredDraft}>
                                  {fieldRequiredDraft && <Check size={12} />}
                                </RequiredCheckboxMark>
                              </RequiredCheckboxButton>
                              <RequiredCheckboxButton
                                onClick={() => setFieldShowOnCardDraft((prev) => !prev)}
                                onDoubleClick={(e) => e.stopPropagation()}
                                title={fieldShowOnCardDraft ? 'Campo exibido na capa' : 'Campo não exibido na capa'}
                              >
                                <RequiredCheckboxMark $active={fieldShowOnCardDraft}>
                                  {fieldShowOnCardDraft && <Eye size={12} />}
                                </RequiredCheckboxMark>
                              </RequiredCheckboxButton>
                            </>
                          ) : (
                            <>
                              <IconButton onClick={() => {
                                setEditingFieldId(field.pipelineField.id)
                                setFieldNameDraft(field.pipelineField.name)
                                setFieldRequiredDraft(Boolean(field.isRequired || field.pipelineField.integrationConfig?.isRequired))
                                setFieldShowOnCardDraft(Boolean(field.showOnCard))
                              }} onDoubleClick={(e) => e.stopPropagation()}><SquarePen size={16} /></IconButton>
                              <IconButton onClick={() => handleDeleteField(step.id, field.pipelineField.id)} onDoubleClick={(e) => e.stopPropagation()}><Trash2 size={16} /></IconButton>
                            </>
                          )}
                        </ActionGroup>
                      </TitleWithActions>
                    </DraggableStepFieldItem>
                  )
                })}
              </> 
              : <>
                {visibleTicketsInStep
                  .map((ticket, idx) =>
          <DraggableTicket
            key={ticket.id}
            ticket={ticket}
            index={idx}
            onReorder={handleTicketReorderInStep}
            onOpen={() => navigate(buildTicketRoute(ticket.id))}
            onEditClick={() => {
              setEditingTicketId(ticket.id)
              setTicketTitleDraft(ticket.title)
            }}
            isEditing={editingTicketId === ticket.id}
            titleDraft={ticketTitleDraft}
            onChangeTitle={setTicketTitleDraft}
            onConfirmEdit={() => saveTicketTitle(ticket.id)}
            onCancelEdit={cancelEditTicket}
            onDelete={() => handleDeleteTicket(ticket.id)}
            onDuplicate={() => handleDuplicateTicket(ticket.id)}
            onCopyLink={() => handleCopyLink(ticket.id)}
            onToggleActive={() => ticket.isActive === false ? handleActivateTicket(ticket.id) : handleDeactivateTicket(ticket.id)}
            onArchive={() => showArchived ? handleUnarchiveTicket(ticket.id) : handleArchiveTicket(ticket.id)}
            showArchived={showArchived}
            coverFieldsInStep={fieldsInSteps}
            resolveIntegratedColorSwatches={resolveIntegratedColorSwatches}
        isModuleView={isModuleView}
        moduleType={moduleType}
                            onToggleSaleStatus={() => handleToggleSaleStatus(ticket.id)}
                            onAdvanceStep={() => handleAdvanceStep(ticket.id)}
                            isLastStep={isLastStepInPipeline(ticket.stepId)}
                            externalData={ticket.externalData}
                            isExpired={ticketExpiredMap[ticket.id]}
                            stepElapsedSeconds={ticketElapsedMap[ticket.id]}
                            stepExecutionTime={ticketExecutionTimeMap[ticket.id]}
                          />
                )}
              </>
              }
              {isProductionModuleView && (() => {
                const totalInStep = totalTicketsByStep[step.id] || 0
                const shownInStep = visibleTicketsInStep.length
                if (shownInStep < totalInStep) {
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '0.5rem',
                      fontSize: '0.72rem',
                      color: '#0090D3',
                      fontWeight: 600,
                    }}>
                      {shownInStep} de {totalInStep} — scroll para mais
                    </div>
                  )
                }
                return null
              })()}
              {newTicketIn === step.id && (
                <TicketItem>
                  <HorizontalDiv style={{backgroundColor: '#F4F4F4', paddingRight: '1rem'}}>
                    <InputForAdd 
                      value={newTicketTitle} 
                      onChange={(e) => setNewTicketTitle(e.target.value)} 
                      placeholder='Título do card' 
                      onKeyDown={handleCreateTicket}
                      autoFocus
                      onBlur={createTicket}
                    /> <X color='#696969' size={20} style={{cursor: 'pointer'}} onClick={() => setNewTicketIn(null)} onDoubleClick={(e) => e.stopPropagation()} />
                  </HorizontalDiv>
                </TicketItem>
              )}
            </ScrollableTicketList>
          </DroppableStepColumn>
        )})}
                    {isConfiguringPipeline && canModifySteps ? (
          <StepColumn><AddStep onClick={() => setAddStepModalIsOpen(true)}><Plus strokeWidth={1.5} /></AddStep></StepColumn>
        ) : null}
      </ScrollingPipeline>

      {stepMenuOpen !== null && ReactDOM.createPortal(
        <StepSettingsMenu
          ref={stepMenuRef}
          top={stepMenuPosition.top}
          left={stepMenuPosition.left}
        >
          <StepSettingsMenuItem onClick={(e) => { e.stopPropagation(); handleToggleStepLock(stepMenuOpen) }}>
            <StepMenuItemLeft>
              <ToggleSwitch>
                <input 
                  type="checkbox" 
                  checked={(steps.find(s => s.id === stepMenuOpen) as any)?.allowCardCreation !== false} 
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => handleToggleStepLock(stepMenuOpen)}
                />
                <span />
              </ToggleSwitch>
              Adição de cards
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepSettingsMenuItem onClick={(e) => { e.stopPropagation() }}>
            <StepMenuItemLeft>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={showInactiveCards}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => handleToggleShowInactiveCards()}
                />
                <span />
              </ToggleSwitch>
              <span onClick={(event) => { event.stopPropagation(); handleToggleShowInactiveCards() }}>
                Mostrar cards inativos
              </span>
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepMenuDivider />
          <StepSettingsMenuItem onClick={() => handleDuplicateStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Copy size={16} />
              Duplicar
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepSettingsMenuItem onClick={() => handleStartRenameStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Pencil size={16} />
              Renomear
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
          <StepSettingsMenuItem $danger onClick={() => handleDeleteStep(stepMenuOpen)}>
            <StepMenuItemLeft>
              <Trash2 size={16} />
              Excluir
            </StepMenuItemLeft>
          </StepSettingsMenuItem>
        </StepSettingsMenu>,
        document.body
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'custom' && (
        <AddFieldOnStep
          key={`custom-${fieldModal.stepId}`}
          stepId={fieldModal.stepId}
          editingField={fieldModal.field}
          closeModal={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
          onToggleCustomMode={(enabled) => {
            if (enabled) {
              setFieldModal({ mode: 'custom', stepId: fieldModal.stepId })
              return
            }
            setFieldModal({ mode: 'integrated', stepId: fieldModal.stepId })
          }}
          onSaved={(updated) => {
            setFieldModal(null)
            if (updated) {
              setFieldsInSteps((prev) => prev.map((item) => item.pipelineField.id === updated.id ? { ...item, pipelineField: { ...item.pipelineField, name: updated.name } } : item))
            }
            fetchFieldsInStepsData()
          }}
        />
      )}

      {isConfiguringPipeline && fieldModal?.mode === 'integrated' && (
        <AddIntegratedFieldModal
          key={`integrated-${fieldModal.stepId}`}
          stepId={fieldModal.stepId}
          closeModal={() => setFieldModal(null)}
          onToggleCustomMode={(enabled) => {
            if (enabled) {
              setFieldModal({ mode: 'custom', stepId: fieldModal.stepId })
              return
            }
            setFieldModal({ mode: 'integrated', stepId: fieldModal.stepId })
          }}
          onSaved={() => {
            setFieldModal(null)
            fetchFieldsInStepsData()
          }}
        />
      )}
      {addStepModalIsOpen && <AddStepInPipeline pipelineId={Number(pipelineId)} closeModal={() => setAddStepModalIsOpen(false)} />}
      {viewOrEditTagsModalIsOpen && <ViewOrEditTags closeModal={() => setViewOrEditTagsModalIsOpen(false)} />}
      {requiredFieldsModal && (
        <FillRequiredFieldsModal
          ticketId={requiredFieldsModal.ticketId}
          targetStepId={requiredFieldsModal.targetStepId}
          onClose={() => setRequiredFieldsModal(null)}
          onSuccess={() => {
            setRequiredFieldsModal(null)
            fetchStepsAndTickets()
          }}
        />
      )}
    </DndProvider>
  )
}

export default Kanban